/**
 * Signal Details Modal - Comprehensive Trading Signal Analysis
 * Shows full technical analysis, indicators, risk metrics, and recommendations
 * Memoized for performance optimization
 */

import { memo } from 'react';
import { Modal, ModalFooter } from '../Modal';
import { Icons } from '../Icons';

interface TradingSignal {
  tokenId: string;
  tokenName: string;
  action: 'buy' | 'sell' | 'hold';
  confidence: number;
  reason: string;
  currentPrice: number;
  suggestedPrice?: number;
  timeframe: string;
}

interface TechnicalIndicators {
  symbol: string;
  timeframe: string;
  rsi: number;
  macd: {
    value: number;
    signal: number;
    histogram: number;
  };
  bollingerBands: {
    upper: number;
    middle: number;
    lower: number;
  };
  movingAverages: {
    sma20: number;
    sma50: number;
    sma200: number;
    ema12: number;
    ema26: number;
  };
  volume: {
    current: number;
    average24h: number;
    ratio: number;
  };
}

interface AggregatedSentiment {
  symbol: string;
  overall: {
    score: number;
    sentiment: 'bullish' | 'neutral' | 'bearish';
    confidence: number;
  };
}

interface RiskMetrics {
  riskReward: string;
  stopLoss: string;
  takeProfit: string;
  positionSize: string;
}

interface SignalDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  signal: TradingSignal | null;
  onExecute?: (signal: TradingSignal) => void;
  technicalIndicators?: TechnicalIndicators;
  sentiment?: AggregatedSentiment;
  riskMetrics?: RiskMetrics;
}

export const SignalDetailsModal = memo(function SignalDetailsModal({
  isOpen,
  onClose,
  signal,
  onExecute,
  technicalIndicators,
  sentiment,
  riskMetrics
}: SignalDetailsModalProps) {
  if (!signal) return null;

  const getSignalColor = (action: string) => {
    switch (action) {
      case 'buy': return '#10b981';
      case 'sell': return '#ef4444';
      case 'hold': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  const getSignalIcon = (action: string) => {
    switch (action) {
      case 'buy': return Icons.TrendingUp;
      case 'sell': return Icons.TrendingDown;
      case 'hold': return Icons.Activity;
      default: return Icons.Activity;
    }
  };

  // Build technical indicators display (use real data if available)
  const technicalIndicatorsDisplay = technicalIndicators ? [
    {
      name: 'RSI (14)',
      value: technicalIndicators.rsi.toFixed(1),
      signal: technicalIndicators.rsi > 70 ? 'Overbought' : technicalIndicators.rsi < 30 ? 'Oversold' : 'Neutral',
      color: technicalIndicators.rsi > 70 ? '#ef4444' : technicalIndicators.rsi < 30 ? '#10b981' : '#f59e0b'
    },
    {
      name: 'MACD',
      value: technicalIndicators.macd.histogram === 0 ? 'N/A' :
             technicalIndicators.macd.histogram > 0 ? 'Bullish' : 'Bearish',
      signal: technicalIndicators.macd.histogram === 0 ? 'Simplified' :
              technicalIndicators.macd.histogram > 0 ? 'Buy' : 'Sell',
      color: technicalIndicators.macd.histogram === 0 ? '#6b7280' :
             technicalIndicators.macd.histogram > 0 ? '#10b981' : '#ef4444'
    },
    {
      name: 'SMA 50',
      value: signal.currentPrice > technicalIndicators.movingAverages.sma50 ? 'Above' : 'Below',
      signal: signal.currentPrice > technicalIndicators.movingAverages.sma50 ? 'Bullish' : 'Bearish',
      color: signal.currentPrice > technicalIndicators.movingAverages.sma50 ? '#10b981' : '#ef4444'
    },
    {
      name: 'Bollinger Bands',
      value: signal.currentPrice > technicalIndicators.bollingerBands.upper ? 'Upper' :
             signal.currentPrice < technicalIndicators.bollingerBands.lower ? 'Lower' : 'Middle',
      signal: signal.currentPrice > technicalIndicators.bollingerBands.upper ? 'Overbought' :
              signal.currentPrice < technicalIndicators.bollingerBands.lower ? 'Oversold' : 'Neutral',
      color: signal.currentPrice > technicalIndicators.bollingerBands.upper ? '#ef4444' :
             signal.currentPrice < technicalIndicators.bollingerBands.lower ? '#10b981' : '#f59e0b'
    },
    {
      name: 'Volume',
      value: technicalIndicators.volume.ratio === 1 ? 'N/A' : `${(technicalIndicators.volume.ratio * 100).toFixed(0)}%`,
      signal: technicalIndicators.volume.ratio === 1 ? 'Simplified' :
              technicalIndicators.volume.ratio > 1.5 ? 'High' :
              technicalIndicators.volume.ratio > 0.8 ? 'Normal' : 'Low',
      color: technicalIndicators.volume.ratio === 1 ? '#6b7280' :
             technicalIndicators.volume.ratio > 1.5 ? '#10b981' : '#f59e0b'
    },
    {
      name: 'SMA 200',
      value: technicalIndicators.movingAverages.sma200 === 0 ? 'N/A' :
             technicalIndicators.movingAverages.sma200 === technicalIndicators.movingAverages.sma50 ? `~$${technicalIndicators.movingAverages.sma200.toFixed(2)}` :
             `$${technicalIndicators.movingAverages.sma200.toFixed(2)}`,
      signal: technicalIndicators.movingAverages.sma200 === 0 ? 'No data' :
              technicalIndicators.movingAverages.sma200 === technicalIndicators.movingAverages.sma50 ? 'Estimated' :
              signal.currentPrice > technicalIndicators.movingAverages.sma200 ? 'Bullish' : 'Bearish',
      color: technicalIndicators.movingAverages.sma200 === 0 ? '#6b7280' :
             technicalIndicators.movingAverages.sma200 === technicalIndicators.movingAverages.sma50 ? '#f59e0b' :
             signal.currentPrice > technicalIndicators.movingAverages.sma200 ? '#10b981' : '#ef4444'
    }
  ] : [
    // Fallback to calculated values if no real data
    { name: 'RSI (14)', value: '—', signal: 'No data', color: '#6b7280' },
    { name: 'MACD', value: '—', signal: 'No data', color: '#6b7280' },
    { name: 'SMA 50', value: '—', signal: 'No data', color: '#6b7280' },
    { name: 'Bollinger Bands', value: '—', signal: 'No data', color: '#6b7280' },
    { name: 'Volume', value: '—', signal: 'No data', color: '#6b7280' },
    { name: 'Support', value: `$${(signal.currentPrice * 0.95).toFixed(2)}`, signal: 'Estimated', color: '#6b7280' }
  ];

  // Use real risk metrics if provided, otherwise calculate from signal
  const riskMetricsDisplay = riskMetrics || {
    riskReward: signal.action === 'buy' ? '1:3.2' : signal.action === 'sell' ? '1:2.8' : '1:1',
    stopLoss: signal.action === 'buy'
      ? `$${(signal.currentPrice * 0.92).toFixed(2)} (-8%)`
      : signal.action === 'sell'
      ? `$${(signal.currentPrice * 1.08).toFixed(2)} (+8%)`
      : 'N/A',
    takeProfit: signal.action === 'buy'
      ? `$${(signal.currentPrice * 1.25).toFixed(2)} (+25%)`
      : signal.action === 'sell'
      ? `$${(signal.currentPrice * 0.78).toFixed(2)} (-22%)`
      : 'N/A',
    positionSize: signal.confidence > 75 ? '15-20%' : signal.confidence > 60 ? '10-15%' : '5-10%'
  };

  // Build AI analysis points (use real sentiment if available)
  const aiAnalysis = sentiment ? [
    {
      icon: sentiment.overall.sentiment === 'bullish' ? '📈' : sentiment.overall.sentiment === 'bearish' ? '📉' : '➡️',
      text: `Market sentiment: ${sentiment.overall.sentiment.toUpperCase()} with ${sentiment.overall.confidence}% confidence (score: ${sentiment.overall.score})`
    },
    {
      icon: '💰',
      text: `AI confidence: ${signal.confidence.toFixed(0)}% for ${signal.action.toUpperCase()} signal`
    },
    {
      icon: technicalIndicators ? '📊' : '⚠️',
      text: technicalIndicators
        ? technicalIndicators.volume.ratio === 1
          ? `RSI: ${technicalIndicators.rsi.toFixed(1)} | MACD: ${technicalIndicators.macd.histogram === 0 ? 'N/A' : technicalIndicators.macd.histogram.toFixed(2)}`
          : `RSI: ${technicalIndicators.rsi.toFixed(1)} | Volume: ${(technicalIndicators.volume.ratio * 100).toFixed(0)}% of average`
        : 'Technical indicators not available - exercise caution'
    },
    {
      icon: '⚠️',
      text: signal.action === 'buy'
        ? sentiment.overall.sentiment === 'bearish'
          ? '⚠️ Contrarian signal - sentiment is bearish but AI suggests buy'
          : 'Sentiment aligns with buy signal'
        : signal.action === 'sell'
        ? sentiment.overall.sentiment === 'bullish'
          ? '⚠️ Contrarian signal - sentiment is bullish but AI suggests sell'
          : 'Sentiment aligns with sell signal'
        : 'Hold signal - wait for clearer market direction'
    }
  ] : [
    { icon: '📊', text: `AI confidence: ${signal.confidence.toFixed(0)}% for ${signal.action.toUpperCase()} signal` },
    { icon: '💰', text: `Current price: $${signal.currentPrice.toLocaleString()} | Timeframe: ${signal.timeframe}` },
    { icon: '📈', text: technicalIndicators ? 'Technical analysis available' : 'Sentiment data unavailable' },
    { icon: '⚠️', text: signal.action === 'buy'
      ? 'Wait for confirmation before entering position'
      : signal.action === 'sell'
      ? 'Consider scaling out gradually'
      : 'Monitor market conditions before taking action' }
  ];

  const SignalIcon = getSignalIcon(signal.action);

  const handleExecute = () => {
    if (onExecute) {
      onExecute(signal);
    }
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title=""
      size="lg"
      footer={
        <ModalFooter>
          <button onClick={onClose} className="btn-ghost">
            Close
          </button>
          {onExecute && (
            <button
              onClick={handleExecute}
              className="btn-primary"
              style={{
                background: getSignalColor(signal.action),
                borderColor: getSignalColor(signal.action)
              }}
            >
              Execute {signal.action.toUpperCase()} Order
            </button>
          )}
        </ModalFooter>
      }
    >
      <div style={{ padding: '8px 0' }}>
        {/* Header with Signal Info */}
        <div style={{
          padding: '24px',
          background: `linear-gradient(135deg, ${getSignalColor(signal.action)}15 0%, ${getSignalColor(signal.action)}05 100%)`,
          borderRadius: '12px',
          marginBottom: '24px',
          border: `1px solid ${getSignalColor(signal.action)}30`
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: getSignalColor(signal.action),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <SignalIcon size={24} style={{ color: 'white' }} />
              </div>
              <div>
                <h3 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '4px' }}>
                  {signal.tokenName}
                </h3>
                <div style={{ fontSize: '14px', color: 'var(--muted)' }}>
                  Current Price: ${signal.currentPrice.toLocaleString()}
                </div>
              </div>
            </div>
            <div style={{
              padding: '8px 16px',
              borderRadius: '8px',
              background: getSignalColor(signal.action),
              color: 'white',
              fontSize: '14px',
              fontWeight: 700,
              textTransform: 'uppercase'
            }}>
              {signal.action}
            </div>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: '12px'
          }}>
            <div style={{
              padding: '12px',
              background: 'var(--card)',
              borderRadius: '8px',
              border: '1px solid var(--border)'
            }}>
              <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px', fontWeight: 600 }}>
                CONFIDENCE
              </div>
              <div style={{ fontSize: '20px', fontWeight: 700, color: getSignalColor(signal.action) }}>
                {signal.confidence.toFixed(0)}%
              </div>
            </div>

            <div style={{
              padding: '12px',
              background: 'var(--card)',
              borderRadius: '8px',
              border: '1px solid var(--border)'
            }}>
              <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px', fontWeight: 600 }}>
                TIMEFRAME
              </div>
              <div style={{ fontSize: '16px', fontWeight: 600 }}>
                {signal.timeframe}
              </div>
            </div>

            {signal.suggestedPrice && (
              <div style={{
                padding: '12px',
                background: 'var(--card)',
                borderRadius: '8px',
                border: '1px solid var(--border)'
              }}>
                <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px', fontWeight: 600 }}>
                  ENTRY PRICE
                </div>
                <div style={{ fontSize: '16px', fontWeight: 600 }}>
                  ${signal.suggestedPrice.toLocaleString()}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* AI Reasoning */}
        <div style={{ marginBottom: '24px' }}>
          <h4 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Icons.Sparkles size={18} style={{ color: 'var(--accent)' }} />
            AI Analysis
          </h4>
          <div style={{
            padding: '16px',
            background: 'var(--bg)',
            borderRadius: '12px',
            fontSize: '14px',
            lineHeight: '1.6',
            color: 'var(--text)',
            marginBottom: '12px'
          }}>
            {signal.reason}
          </div>
          <div style={{ display: 'grid', gap: '8px' }}>
            {aiAnalysis.map((point, idx) => (
              <div key={idx} style={{
                display: 'flex',
                gap: '12px',
                padding: '10px',
                background: 'var(--bg)',
                borderRadius: '8px',
                fontSize: '13px'
              }}>
                <span style={{ fontSize: '18px' }}>{point.icon}</span>
                <span style={{ flex: 1 }}>{point.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Technical Indicators */}
        <div style={{ marginBottom: '24px' }}>
          <h4 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Icons.Activity size={18} style={{ color: 'var(--accent)' }} />
            Technical Indicators
          </h4>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
            gap: '12px'
          }}>
            {technicalIndicatorsDisplay.map((indicator, idx) => (
              <div key={idx} style={{
                padding: '12px',
                background: 'var(--bg)',
                borderRadius: '8px',
                border: '1px solid var(--border)'
              }}>
                <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '6px', fontWeight: 600 }}>
                  {indicator.name}
                </div>
                <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '4px' }}>
                  {indicator.value}
                </div>
                <div style={{
                  fontSize: '12px',
                  fontWeight: 600,
                  color: indicator.color
                }}>
                  {indicator.signal}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Risk Management */}
        <div>
          <h4 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Icons.Shield size={18} style={{ color: 'var(--accent)' }} />
            Risk Management
          </h4>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '12px'
          }}>
            <div style={{
              padding: '14px',
              background: 'var(--bg)',
              borderRadius: '8px',
              border: '1px solid var(--border)'
            }}>
              <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '6px', fontWeight: 600 }}>
                RISK:REWARD RATIO
              </div>
              <div style={{ fontSize: '18px', fontWeight: 700, color: '#10b981' }}>
                {riskMetricsDisplay.riskReward}
              </div>
            </div>

            <div style={{
              padding: '14px',
              background: 'var(--bg)',
              borderRadius: '8px',
              border: '1px solid var(--border)'
            }}>
              <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '6px', fontWeight: 600 }}>
                STOP LOSS
              </div>
              <div style={{ fontSize: '15px', fontWeight: 600, color: '#ef4444' }}>
                {riskMetricsDisplay.stopLoss}
              </div>
            </div>

            <div style={{
              padding: '14px',
              background: 'var(--bg)',
              borderRadius: '8px',
              border: '1px solid var(--border)'
            }}>
              <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '6px', fontWeight: 600 }}>
                TAKE PROFIT
              </div>
              <div style={{ fontSize: '15px', fontWeight: 600, color: '#10b981' }}>
                {riskMetricsDisplay.takeProfit}
              </div>
            </div>

            <div style={{
              padding: '14px',
              background: 'var(--bg)',
              borderRadius: '8px',
              border: '1px solid var(--border)'
            }}>
              <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '6px', fontWeight: 600 }}>
                POSITION SIZE
              </div>
              <div style={{ fontSize: '15px', fontWeight: 600 }}>
                {riskMetricsDisplay.positionSize}
              </div>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <div style={{
          marginTop: '24px',
          padding: '12px',
          background: 'rgba(234, 179, 8, 0.1)',
          border: '1px solid rgba(234, 179, 8, 0.3)',
          borderRadius: '8px',
          fontSize: '12px',
          color: 'var(--muted)',
          display: 'flex',
          gap: '8px'
        }}>
          <Icons.AlertTriangle size={16} style={{ color: '#eab308', flexShrink: 0, marginTop: '2px' }} />
          <div>
            <strong>Risk Warning:</strong> Trading signals are AI-generated predictions and should not be considered as financial advice.
            Always do your own research and only invest what you can afford to lose.
          </div>
        </div>
      </div>
    </Modal>
  );
});

SignalDetailsModal.displayName = 'SignalDetailsModal';
